import * as Sentry from "@sentry/gatsby";

/**
 * Reusable API fetching utility with Sentry integration
 * 
 * @param {string} url - The URL to fetch from
 * @param {string} description - Description of what's being fetched (for logging)
 * @param {Object} context - Additional context for Sentry logging
 * @returns {Promise<Object|null>} - Parsed JSON response or null on error
 */
export const safeFetch = async (url, description, context = {}) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching ${description}:`, error);
    
    // Log to Sentry with context
    Sentry.captureException(error, {
      tags: {
        section: context.section || 'api_fetch',
        operation: 'data_fetch',
        description: description
      },
      contexts: {
        request: {
          url: url,
          ...context
        }
      }
    });
    
    return null;
  }
};

/**
 * Safely parse units data from API response
 * 
 * @param {Object} data - Raw API response
 * @param {string} description - Description for error logging
 * @param {Object} context - Additional context for Sentry logging
 * @returns {Array} - Parsed units array or empty array on error
 */
export const safeParseUnitsData = (data, description = '', context = {}) => {
  if (!data || !data.res) {
    return [];
  }
  
  try {
    return JSON.parse(data.res);
  } catch (error) {
    console.error(`Error parsing units data ${description}:`, error);
    
    // Log to Sentry with context
    Sentry.captureException(error, {
      tags: {
        section: context.section || 'data_parsing',
        operation: 'data_parsing'
      },
      contexts: {
        parsing: {
          description: description,
          data_type: 'units_data',
          ...context
        }
      },
      extra: {
        raw_data: data
      }
    });
    
    return [];
  }
};

/**
 * Safely parse deals data from API response
 * 
 * @param {Object} data - Raw API response
 * @param {string} description - Description for error logging
 * @param {Object} context - Additional context for Sentry logging
 * @returns {Array} - Parsed deals array or empty array on error
 */
export const safeParseDealsData = (data, description = 'deals', context = {}) => {
  if (!data || !data.res) {
    return [];
  }
  
  try {
    return JSON.parse(data.res);
  } catch (error) {
    console.error(`Error parsing deals data ${description}:`, error);
    
    // Log to Sentry with context
    Sentry.captureException(error, {
      tags: {
        section: context.section || 'data_parsing',
        operation: 'deals_parsing'
      },
      contexts: {
        parsing: {
          description: description,
          data_type: 'deals_data',
          ...context
        }
      },
      extra: {
        raw_deals_data: data
      }
    });
    
    return [];
  }
};

/**
 * Generic data parser with error handling
 * 
 * @param {Object} data - Raw data to parse
 * @param {Function} parseFunction - Custom parsing function
 * @param {string} description - Description for error logging
 * @param {Object} context - Additional context for Sentry logging
 * @returns {*} - Parsed data or fallback value on error
 */
export const safeParseData = (data, parseFunction, description, context = {}, fallback = null) => {
  try {
    return parseFunction(data);
  } catch (error) {
    console.error(`Error parsing ${description}:`, error);
    
    // Log to Sentry with context
    Sentry.captureException(error, {
      tags: {
        section: context.section || 'data_parsing',
        operation: 'custom_parsing'
      },
      contexts: {
        parsing: {
          description: description,
          ...context
        }
      },
      extra: {
        raw_data: data
      }
    });
    
    return fallback;
  }
};

/**
 * Fetch multiple URLs with individual error handling
 * 
 * @param {Array} fetchConfigs - Array of {url, description, context} objects
 * @param {Object} globalContext - Global context applied to all fetches
 * @returns {Promise<Array>} - Array of results (null for failed fetches)
 */
export const safeFetchMultiple = async (fetchConfigs, globalContext = {}) => {
  const promises = fetchConfigs.map(({ url, description, context = {} }) =>
    safeFetch(url, description, { ...globalContext, ...context })
  );
  
  return Promise.all(promises);
};

/**
 * Create a standardized error logging function for specific sections
 * 
 * @param {string} section - The section name (e.g., 'floorplan_template')
 * @param {Object} baseContext - Base context to include in all logs
 * @returns {Function} - Configured error logging function
 */
export const createSectionErrorLogger = (section, baseContext = {}) => {
  return (error, operation, additionalContext = {}) => {
    console.error(`Error in ${section} - ${operation}:`, error);
    
    Sentry.captureException(error, {
      tags: {
        section: section,
        operation: operation
      },
      contexts: {
        [section]: {
          ...baseContext,
          ...additionalContext
        }
      }
    });
  };
};